import React, { useEffect, useState } from 'react';
import ReactConfetti from 'react-confetti';
import { Card, CardContent, Typography, Box, Chip, Avatar, Skeleton } from '@mui/material';
import { Rank1, Rank2, Rank3 } from './CustomIcons';
import { useContent } from '../../../../context/ContentContext';
import DynamicsLogo from '../../../../components/images/DynamicsLogo';

function stringAvatar(name) {
    return {
        sx: {
            bgcolor: stringToColor(name),
        },
        children: `${name.split(' ')[0][0]}${name.split(' ')[1] ? name.split(' ')[1][0] : ''}`,
    };
}

function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        color += ('00' + ((hash >> (i * 8)) & 0xff).toString(16)).slice(-2);
    }
    return color;
}

const CONFETTI_KEY = 'confettiTriggered';

const Top3 = ({ topAgentData }) => {
    const { loading, error } = useContent(); // Use context to get top agent data
    const [confettiActive, setConfettiActive] = useState(false);

    useEffect(() => {
        const now = Date.now();
        const confettiData = JSON.parse(localStorage.getItem(CONFETTI_KEY));

        if (!confettiData || now - confettiData.timestamp > 24 * 60 * 60 * 1000) {
            setConfettiActive(true);
            localStorage.setItem(CONFETTI_KEY, JSON.stringify({ timestamp: now }));

            const confettiTimer = setTimeout(() => {
                setConfettiActive(false); // Stop confetti after 5 seconds
            }, 5000);

            return () => clearTimeout(confettiTimer); // Cleanup timer
        }
    }, [topAgentData]);

    if (loading) {
        // Render skeletons while loading
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
                {[...Array(3)].map((_, index) => (
                    <Card
                        key={index}
                        sx={{
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            border: 'none',
                        }}
                    >
                        <CardContent
                            sx={{
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                            }}
                        >
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="text" width={60} height={20} />
                            <Skeleton variant="text" width={100} height={15} />
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <DynamicsLogo
                src="images/NoResultFound.webp"
                width="300px"
            />
        );
    }

    if (!topAgentData || !Array.isArray(topAgentData) || topAgentData.length === 0) {
        return (
            <DynamicsLogo
                src="images/NoResultFound.webp"
                width="300px"
            />
        );
    }

    return (
        <Card
            sx={{
                width: '100%',
                backgroundColor: 'transparent',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 0,
                boxShadow: 'none',
                border: 'none',
                position: 'relative',
            }}
        >
            {confettiActive && (
                <ReactConfetti
                    width={1920}
                    height={1080}
                    recycle={false}
                    numberOfPieces={200}
                    confettiSource={{ x: 0, y: 0, w: 1920, h: 0 }}
                />
            )}

            <Box
                sx={{
                    position: 'absolute',
                    top: '82%',
                    left: '8%',
                    transform: 'translate(-50%, -50%) rotate(-90deg)',
                    fontSize: '3rem',
                    color: 'rgba(0, 0, 0, 0.1)',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    zIndex: 0,
                    whiteSpace: 'nowrap',
                }}
            >
                TOP 3
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                }}
            >
                {/* Rank 2 */}
                {topAgentData[1] && (
                    <Card
                        sx={{
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            border: 'none',
                        }}
                    >
                        <CardContent
                            sx={{
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                flex: 1,
                            }}
                        >
                            <Avatar {...stringAvatar(topAgentData[1].agent)} sx={{ width: 40 }} />
                            <Typography variant="body2">{topAgentData[1].agent}</Typography>
                            <Chip
                                label={`Rp.${topAgentData[1].total_income.toLocaleString()}`}
                                color="primary"
                                size="small"
                                sx={{
                                    marginTop: 1,
                                    '& .MuiChip-label': {
                                        fontSize: '0.5rem',
                                    },
                                }}
                            />
                            <Rank2
                                sx={{
                                    width: '100%',
                                    fontSize: 150,
                                }}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Rank 1 */}
                {topAgentData[0] && (
                    <Card
                        sx={{
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            border: 'none',
                        }}
                    >
                        <CardContent
                            sx={{
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                            }}
                        >
                            <img src="/images/Crown.webp" alt="Crown" style={{ width: 80, height: 'auto' }} />
                            <Avatar {...stringAvatar(topAgentData[0].agent)} sx={{ width: 40 }} />
                            <Typography variant="body2">{topAgentData[0].agent}</Typography>
                            <Chip
                                label={`Rp.${topAgentData[0].total_income.toLocaleString()}`}
                                color="primary"
                                sx={{
                                    marginTop: 1,
                                    '& .MuiChip-label': {
                                        fontSize: '0.5rem',
                                    },
                                }}
                            />
                            <Rank1
                                sx={{
                                    width: '100%',
                                    fontSize: 200,
                                }}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Rank 3 */}
                {topAgentData[2] && (
                    <Card
                        sx={{
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            border: 'none',
                        }}
                    >
                        <CardContent
                            sx={{
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                            }}
                        >
                            <Avatar {...stringAvatar(topAgentData[2].agent)} sx={{ width: 40 }} />
                            <Typography variant="body2">{topAgentData[2].agent}</Typography>
                            <Chip
                                label={`Rp.${topAgentData[2].total_income.toLocaleString()}`}
                                color="primary"
                                sx={{
                                    marginTop: 1,
                                    '& .MuiChip-label': {
                                        fontSize: '0.5rem',
                                    },
                                }}
                            />
                            <Rank3
                                sx={{
                                    width: '100%',
                                    fontSize: 135,
                                }}
                            />
                        </CardContent>
                    </Card>
                )}
            </Box>
        </Card>
    );
};

export default Top3;
